import { Breadcrumbs, Grid, Paper, Typography } from '@mui/material';

import { useTheme } from '@mui/material/styles';

import { useSnackbar } from 'notistack';

import { useEffect, useState } from 'react';

import { SubmitHandler } from 'react-hook-form';

import { useMutation, useQuery } from 'react-query';

import { useLocation, useNavigate, useParams } from 'react-router';

import { Link } from 'react-router-dom';

import * as api from '../../api/app/assetApi';

import * as commonApi from '../../api/app/commonApi';

import { ApiError } from '../../components/core/ApiError/ApiError';

import { Loader } from '../../components/core/Loader/Loader';

import { PageContent } from '../../components/core/PageContent/PageContent';

import { PageHeader } from '../../components/core/PageHeader/PageHeader';

import { useAuth } from '../../contexts/authWindows';

import { useTranslation } from '../../contexts/translation';

import { Asset, AssetInputs, assetTypes } from '../../types/app/asset.types';

import { UserRoles } from '../../types/core/auth.types';

import { dictionary } from '../../utils/core/dictionary';

import { convertDataFromInputToCreateRequest } from '../../utils/core/functions/asset.functions';

import { paths } from '../../utils/core/routes';

import { AssetForm } from './AssetForm';

import { useStyles } from './AssetForm.styles';

export function AssetCreate() {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useAuth();
  const { state } = useLocation();
  const isReadOnly = user?.roles.includes(UserRoles.Admin) ? false : true;
  const [assetFullPath, setAssetFullPath] = useState<Asset[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { mutate: mutateCreateAsset } = useMutation(api.createAsset);
  const { parentAssetId } = useParams();
  const [defaultValues, setDefaultValues] = useState<AssetInputs>();
  const { data: isUsedHydra } = useQuery<boolean>('useHydra', commonApi.useHydraDataService);
  const {
    data: parentAsset,
    isLoading,
    isError,
  } = useQuery<Asset>(['asset', parentAssetId], () => api.getAsset(Number(parentAssetId)), {
    enabled: parentAssetId !== undefined && parentAssetId !== null,
  });

  useEffect(() => {
    if (!state) return;
    setAssetFullPath(state as Asset[]);
  }, [state]);

  useEffect(() => {
    if (!parentAsset) return;

    setDefaultValues({
      assetType: assetTypes[0].code,
      groupId: parentAsset ? parentAsset.groupId! : undefined,
      name: '',
      influxMachineName: parentAsset?.influxMachineName ? parentAsset?.influxMachineName + '.' : '',
      useWithDashboards: false,
      grafanaFolderUId: '',
      hydraMachineNumber: null,
      loadPlanFromHydra: false,
      loadPlanFromHydraMultiplicity: false,
      loadPlanFromHydraMultiple: 1,
      unitOverride: null,
    });
  }, [parentAsset]);

  const handleOnSubmit: SubmitHandler<AssetInputs> = (data: AssetInputs) => {
    const requestData = convertDataFromInputToCreateRequest({
      ...data,
      parentAssetId: Number(parentAssetId),
    });

    if (data.loadPlanFromHydraMultiple !== '') {
      requestData.loadPlanFromHydraMultiple = parseFloat(
        data.loadPlanFromHydraMultiple?.toString().replace(',', '.'),
      );
    }
    if (data.loadPlanFromHydraMultiple === '') requestData.loadPlanFromHydraMultiple = 1;
    mutateCreateAsset(
      { ...requestData },
      {
        onSuccess: (asset: Asset) => {
          enqueueSnackbar(t('page.asset.item-created'), { variant: 'success' });
          navigate(`${paths.ASSETS_DETAIL}/${asset.id}`);
        },
        onError: (result) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || (parentAssetId && !parentAsset) || isUsedHydra === undefined) return <Loader />;

  return (
    <>
      {assetFullPath.length > 0 ? (
        <Breadcrumbs aria-label="breadcrumb">
          {assetFullPath.map((asset: Asset) => {
            return (
              <Link
                key={asset.id}
                to={`${paths.ASSETS_DETAIL}/${asset.id}`}
                style={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  textDecoration: 'underline',
                }}
              >
                <Typography component={'h4'} variant={'h4'}>
                  {asset.name}
                </Typography>
              </Link>
            );
          })}
          <Typography component={'h4'} variant={'h4'} sx={{ color: theme.palette.primary.dark }}>
            {dictionary.CREATE}
          </Typography>
        </Breadcrumbs>
      ) : (
        <PageHeader title={t('page.asset.headline-create')} />
      )}
      <PageContent>
        <Grid container spacing={1}>
          <Grid item xs={12} md={8} lg={4}>
            <Paper className={classes.paper}>
              <AssetForm
                onSubmit={handleOnSubmit}
                onBack={() =>
                  navigate(
                    parentAssetId
                      ? `${paths.ASSETS_DETAIL}/${parentAssetId}`
                      : `${paths.ASSETS_LIST}`,
                  )
                }
                defaultValues={defaultValues}
                isUsedHydra={isUsedHydra}
                isReadOnly={isReadOnly}
              />
            </Paper>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
